import { Entities } from '@gopeerproject/ui-kit';
import Delta from 'quill-delta';
import Quill from 'quill';
const Embed = Quill.import('blots/embed');
/* @ts-expect-error quill does not export types for blots hence ... */
class EntityBlot extends Embed {
    static create(value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        const node = super.create(value);
        node.setAttribute('data-value', value);
        const text = document.createTextNode(`{${Entities[value].text}}`);
        node.appendChild(text);
        return node;
    }
    static value(domNode) {
        return domNode.getAttribute('data-value');
    }
}
EntityBlot.blotName = 'entity';
EntityBlot.tagName = 'span';
EntityBlot.className = 'ql-entity';
const HyperLinkRegExp = /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gim;
const formatStringToLink = (str) => str.includes('http') ? str : `http://${str}`;
const linkFormatter = (editor) => {
    /**
     * isAutomaticallyFormatted: @Boolean
     * Creates new property for Quill Delta Attributes Object
     * It's used for determining if a message having a link attribute
     * is created by LinkInput of the editor or is created automatically by hyperLinksListener method
     * if the user created a link with LinkInput and then just continued typing, we should not interfere
     */
    const Parchment = Quill.import('parchment');
    const offsetAttributor = new Parchment.Attributor.Attribute('isAutomaticallyFormatted', 'isAutomaticallyFormatted', { scope: Parchment.Scope.INLINE });
    Quill.register(offsetAttributor);
    editor.on('text-change', () => {
        linkListener(editor);
    });
};
const linkListener = (editor) => {
    var _a, _b, _c, _d;
    //current state of quill
    const ops = editor.getContents().ops;
    // we need this for finding exact index for links
    const contentAsText = editor.getText();
    const length = ops.length;
    // storing updates in this delta object
    const newDelta = new Delta();
    const newInsert = (_a = ops[length - 1]) === null || _a === void 0 ? void 0 : _a.insert;
    /**
     * We should take previous element into consideration if it's created automatically and
     * if char === ' ' means that user finished typing link and we can move on
     * if char === ',' , can't be part of the url, so this means user is just using it as a comma
     * if char === '\n' quill adds this automatically after updateContent call
     */
    const isPreviousAutomaticallyCreatedLink = length > 1 &&
        !!((_b = ops[length - 2].attributes) === null || _b === void 0 ? void 0 : _b.isAutomaticallyFormatted) &&
        ![' ', '\n', ','].includes((_c = newInsert[0]) !== null && _c !== void 0 ? _c : '');
    if (isPreviousAutomaticallyCreatedLink) {
        const oldValue = (_d = ops[length - 2]) === null || _d === void 0 ? void 0 : _d.insert;
        const indexOfLink = contentAsText.indexOf(oldValue);
        const newValue = oldValue + newInsert;
        /**
         * leave everything as it is before link chunk
         * remove link chunk
         * add new link with updated href and name
         * isAutomaticallyFormatted for differing automatically and manually created links
         */
        newDelta
            .retain(indexOfLink)
            .delete(contentAsText.length - indexOfLink)
            .insert(newValue, {
            link: formatStringToLink(newValue),
            isAutomaticallyFormatted: true
        });
    }
    else {
        const matches = newInsert.match(HyperLinkRegExp);
        if (!matches)
            return;
        matches.forEach((link) => {
            var _a;
            const index = contentAsText.indexOf(link);
            /**
             * leave everything as it is before link chunk
             * add new attributes to link chunk
             * isAutomaticallyFormatted for differing automatically and manually created links
             */
            newDelta.retain(index).retain(link.length, Object.assign(Object.assign({}, (_a = ops[length - 1]) === null || _a === void 0 ? void 0 : _a.attributes), { link: formatStringToLink(link), isAutomaticallyFormatted: true }));
        });
    }
    editor.updateContents(newDelta);
};
const checkFormat = (editor, tools) => {
    const range = editor.getSelection();
    if (!range)
        return {};
    const format = editor.getFormat();
    const next = {};
    const patch = [
        ...(format.bold ? ['bold'] : []),
        ...(format.italic ? ['italic'] : []),
        ...(format.strike ? ['strike'] : []),
        ...(format.underline ? ['underline'] : []),
        ...(format.link ? ['link'] : []),
        ...(format.list && format.list === 'bullet' ? ['bullet'] : []),
        ...(format.list && format.list === 'ordered' ? ['ordered'] : []),
        ...(format.link && tools.includes('link-tooltip') ? ['link-tooltip'] : [])
    ];
    if (!patch.reduce((acc, v) => acc && tools.includes(v), tools.length === patch.length))
        next.tools = patch;
    if (patch.includes('link')) {
        const [leaf, offset] = editor.getLeaf(range.index);
        const { left, top } = editor.getBounds(range.index - offset, leaf.domNode.length);
        next.linkTooltip = Object.assign({ top, left }, getLink(editor));
    }
    return next;
};
const getLink = (editor) => {
    const range = editor.getSelection();
    if (!range)
        return { title: '', value: '' };
    const [leaf] = editor.getLeaf(range.index);
    const format = editor.getFormat();
    if (format.link)
        return { title: leaf.text, value: format.link };
    else if (range.length)
        return {
            title: editor.getText(range.index, range.length),
            value: ''
        };
    else
        return { title: '', value: '' };
};
const setLink = (editor, lastSelection, value, title) => {
    var _a;
    const range = editor.getSelection();
    if (!range)
        return;
    const [leaf, offset] = editor.getLeaf(range.index);
    const format = editor.getFormat();
    value = value.indexOf('http') !== 0 ? `http://${value}` : value;
    if (format.link) {
        editor.deleteText(range.index - offset, leaf.domNode.length);
        editor.insertText(range.index - offset, title);
        editor.formatText(range.index - offset, title.length, 'link', value);
    }
    else if (title && !range.length) {
        editor.insertText((_a = lastSelection === null || lastSelection === void 0 ? void 0 : lastSelection.index) !== null && _a !== void 0 ? _a : 0, title, 'link', value);
    }
    else if (title && range.length) {
        editor.deleteText(range.index, range.length);
        editor.insertText(range.index, title);
        editor.formatText(range.index, title.length, 'link', value);
    }
};
const clipboardMatcher = (_, delta) => {
    const images = delta.ops.filter((op) => typeof op.insert === 'object' &&
        Object.prototype.hasOwnProperty.call(op.insert, 'image'));
    delta.ops = delta.ops
        .map((op) => {
        var _a, _b;
        (_a = op.attributes) === null || _a === void 0 ? true : delete _a.background;
        (_b = op.attributes) === null || _b === void 0 ? true : delete _b.color;
        return op;
    })
        .filter((op) => !images.includes(op));
    return delta;
};
export { EntityBlot, getLink, setLink, checkFormat, clipboardMatcher, linkFormatter };
//# sourceMappingURL=index.js.map